import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type { UserProfile } from "@/components/feature/Sidebar";
import { inputService } from "@/services/api";
import { mapDbInsumoToFrontend } from "@/services/mapping";

function getStoredUser(): UserProfile | null {
  const stored = localStorage.getItem("convivencia_user");
  return stored ? JSON.parse(stored) : null;
}

export default function DocenteDevolucionesPage() {
  const [user] = useState<UserProfile | null>(getStoredUser);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [requests, setRequests] = useState<any[]>([]);

  useEffect(() => {
    async function loadData() {
      if (!user) return;
      try {
        setLoading(true);
        setError("");
        const data = await inputService.listSolicitados();
        if (Array.isArray(data)) {
          const mapped = data
            .map(mapDbInsumoToFrontend)
            .filter((t) => (String(t.docenteId) === String(user.id) || t.docenteName === user.nombre) && t.estado === "devuelto_correccion");
          setRequests(mapped);
        }
      } catch (err: any) {
        console.error("Error loading returned inputs:", err);
        setError("Error al cargar devoluciones: " + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [user]);

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="flex items-center justify-center w-8 h-8">
          <i className="ri-loader-4-line text-foreground-300 text-2xl animate-spin" />
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div>
        <button
          onClick={() => navigate("/docente")}
          className="text-xs font-medium text-foreground-500 hover:text-foreground-700 cursor-pointer mb-2 flex items-center gap-1"
        >
          <span className="flex items-center justify-center w-3 h-3">
            <i className="ri-arrow-left-line" />
          </span>
          Volver al panel
        </button>
        <div className="flex items-center gap-3">
          <h1 className="text-xl md:text-2xl font-bold text-foreground-950 tracking-tight">Solicitudes Devueltas</h1>
          {requests.length > 0 && (
            <span className="badge-modern bg-red-100 text-red-700 border border-red-200/50">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              {requests.length} para corregir
            </span>
          )}
        </div>
        <p className="text-sm text-foreground-500 mt-1">Informes que requieren ajustes antes de ser aprobados</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <i className="ri-error-warning-line mr-1" />{error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500 mr-2" /> Cargando devoluciones...
        </div>
      ) : requests.length > 0 ? (
        <div className="space-y-4">
          {/* Alert banner */}
          <div className="alert-card bg-orange-50 border-orange-200/70 p-5 rounded-2xl border">
            <div className="flex items-start gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-orange-100 flex-shrink-0">
                <i className="ri-information-line text-orange-500 text-lg" />
              </span>
              <div>
                <p className="text-sm font-bold text-orange-800">Atención requerida</p>
                <p className="text-xs text-orange-600 mt-1 leading-relaxed">
                  Tienes {requests.length} informe{requests.length !== 1 ? "s" : ""} devuelto{requests.length !== 1 ? "s" : ""} para corrección.
                  Revisa las observaciones del solicitante, ajusta el lenguaje según las sugerencias de la IA y vuelve a enviar.
                  Los informes con lenguaje subjetivo, juicios de valor o información insuficiente serán devueltos.
                </p>
              </div>
            </div>
          </div>

          {/* List */}
          {requests.map((ti, idx) => {
            return (
              <div key={ti.id} className="card-modern overflow-hidden border border-background-200 bg-white p-5 rounded-2xl animate-fade-in-up" style={{ animationDelay: `${idx * 40}ms` }}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
                      <p className="text-sm font-bold text-foreground-900">{ti.tipoInsumo}</p>
                      <span className="badge-modern bg-red-50 text-red-700 border border-red-200/50">
                        Devuelto para corrección
                      </span>
                    </div>
                    <p className="text-xs text-foreground-500">Estudiante: {ti.studentName} ({ti.studentCurso})</p>
                    <p className="text-xs text-foreground-400 mt-1 line-clamp-2">{ti.descripcionSolicitud}</p>
                    {ti.respuesta && (
                      <div className="mt-3 bg-red-50/60 rounded-lg p-3 border border-red-100/60">
                        <p className="text-[11px] font-bold text-red-700 mb-1">Respuesta original (requiere adjustments):</p>
                        <p className="text-xs text-foreground-600 italic line-clamp-3">&ldquo;{ti.respuesta}&rdquo;</p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                      <p className="text-xs text-foreground-500">Enviado: {ti.submittedAt || "—"}</p>
                      <p className="text-xs text-red-500 font-bold">Límite original: {ti.fechaLimite}</p>
                    </div>
                    <button
                      onClick={() => navigate(`/docente/solicitudes/${ti.id}`)}
                      className="btn-modern bg-orange-500 text-white hover:bg-orange-600 active:scale-[0.98] px-4 py-2 text-sm cursor-pointer rounded-xl font-medium"
                    >
                      Corregir informe
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-3">
            <span className="flex items-center justify-center w-8 h-8">
              <i className="ri-check-double-line text-emerald-400 text-2xl" />
            </span>
          </div>
          <h3 className="text-base font-bold text-foreground-800 mb-1">Sin devoluciones</h3>
          <p className="text-sm text-foreground-500 max-w-md mx-auto">
            No tienes informes devueltos para corrección. ¡Buen trabajo!
          </p>
        </div>
      )}
    </div>
  );
}